// src/pages/educator/components/EducatorLayout.js
import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  FaHome,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaCog,
  FaBars,
  FaBook,
  FaEnvelope,
  FaSignOutAlt,
  FaBullhorn,
  FaVideo,
  FaChartBar,
} from 'react-icons/fa';
import { AuthContext } from '../../auth/AuthContext';
import api from '../../api/api';
import AppShell from '../../layouts/AppShell';

// Only add a version when we actually have an updatedAt value
const buildAvatarUrl = (raw, updatedAt, apiBase) => {
  if (!raw) return '/avatar.png';

  const base = /^https?:\/\//i.test(raw)
    ? raw.split('?')[0]
    : (raw.startsWith('/uploads/')
      ? `${apiBase}${raw}`.split('?')[0]
      : `${apiBase}/uploads/avatars/${raw}`.split('?')[0]);

  const stamp = updatedAt ? new Date(updatedAt).getTime() : null;
  if (!stamp) return base;

  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}v=${stamp}`;
};

const EducatorLayout = ({ children }) => {
  const [fetchedAvatar, setFetchedAvatar] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, setUser } = useContext(AuthContext) ?? {};
  const [counts, setCounts] = useState({ messages: 0, announcements: 0, classes: 0 });
  const apiBase = process.env.REACT_APP_API_BASE_URL
    || process.env.REACT_APP_API_URL
    || api?.defaults?.baseURL
    || '';

  const navLinks = [
    { key: 'dashboard', name: 'Dashboard', path: '/educator', icon: <FaHome /> },
    { key: 'classes', name: 'My Classes', path: '/educator/classes', icon: <FaChalkboardTeacher /> },
    { key: 'lessons', name: 'Lessons', path: '/educator/lessons', icon: <FaBook /> },
    { key: 'assessments', name: 'Assessments', path: '/educator/assessments', icon: <FaBook /> },
    { key: 'resources', name: 'Resources', path: '/educator/resources', icon: <FaBook /> },
    { key: 'analytics', name: 'Analytics', path: '/educator/analytics', icon: <FaChartBar /> },
    { key: 'students', name: 'Students', path: '/educator/students', icon: <FaUserGraduate /> },
    { key: 'recordings', name: 'Recordings', path: '/educator/recordings', icon: <FaVideo /> },
    { key: 'messages', name: 'Messages', path: '/educator/messages', icon: <FaEnvelope /> },
    { key: 'announcements', name: 'Announcements', path: '/educator/announcements', icon: <FaBullhorn /> },
    { key: 'support', name: 'Support', path: '/educator/support', icon: <FaEnvelope /> },
    { key: 'settings', name: 'Settings', path: '/educator/settings', icon: <FaCog /> },
  ];

  const displayName = user?.name || 'Educator';

  useEffect(() => {
    let cancelled = false;
    const needFetch = user?._id && (!user?.profilePictureUrl || user.profilePictureUrl?.startsWith('/'));
    const fetchFresh = async () => {
      try {
        const { data } = await api.get(`/users/${user._id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (cancelled) return;
        setFetchedAvatar(data?.profilePictureUrl || null);
        if (typeof setUser === 'function') {
          setUser((prev) => ({ ...(prev || {}), ...data }));
          try {
            localStorage.setItem('user', JSON.stringify({ ...(user || {}), ...data }));
          } catch {}
        }
      } catch {}
    };
    if (needFetch) fetchFresh();
    return () => { cancelled = true; };
  }, [user?._id, token, setUser, user]);

  const resolvedAvatar = useMemo(() => {
    if (fetchedAvatar) {
      return buildAvatarUrl(fetchedAvatar, user?.updatedAt, apiBase);
    }
    const raw = user?.profilePictureUrl || user?.profilePictureKey;
    return buildAvatarUrl(raw, user?.updatedAt, apiBase);
  }, [fetchedAvatar, user?.profilePictureUrl, user?.profilePictureKey, user?.updatedAt, apiBase]);

  // Notifications: polling for messages/announcements/classes

  const getLastSeen = useCallback((k) => { try { const key = `lms:lastSeen:educator:${k}:${user?._id || 'anon'}`; return Number(localStorage.getItem(key)) || 0; } catch { return 0; } }, [user?._id]);
  const setLastSeen = useCallback((k, t = Date.now()) => { try { const key = `lms:lastSeen:educator:${k}:${user?._id || 'anon'}`; localStorage.setItem(key, String(t)); } catch {} }, [user?._id]);
  const countNewerThan = (items, lastSeen) => items.reduce((acc, item) => {
    const createdAt = item?.createdAt ? new Date(item.createdAt).getTime() : 0;
    return createdAt > lastSeen ? acc + 1 : acc;
  }, 0);

  useEffect(() => {
    if (location.pathname.startsWith('/educator/messages')) { setLastSeen('messages'); setCounts((d) => ({ ...d, messages: 0 })); }
    if (location.pathname.startsWith('/educator/announcements')) { setLastSeen('announcements'); setCounts((d) => ({ ...d, announcements: 0 })); }
    if (location.pathname.startsWith('/educator/classes')) { setLastSeen('classes'); setCounts((d) => ({ ...d, classes: 0 })); }
  }, [location?.pathname, setLastSeen]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const checkMessages = async () => {
      try {
        const { data } = await api.get('/messages');
        const list = Array.isArray(data) ? data : [];
        const incoming = list.filter((item) => item?.sender?._id !== user?._id);
        const lastSeen = getLastSeen('messages');
        return countNewerThan(incoming, lastSeen);
      } catch {
        return null;
      }
    };

    const checkAnnouncements = async () => {
      try {
        const { data } = await api.get('/notifications');
        const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const lastSeen = getLastSeen('announcements');
        return countNewerThan(list, lastSeen);
      } catch {
        return null;
      }
    };

    const checkClasses = async () => {
      try {
        const { data } = await api.get('/sessions');
        const arr = Array.isArray(data) ? data : [];
        const lastSeen = getLastSeen('classes');
        return countNewerThan(arr, lastSeen);
      } catch {
        return null;
      }
    };

    const run = async () => {
      const [msgCount, annCount, classCount] = await Promise.all([
        checkMessages(),
        checkAnnouncements(),
        checkClasses(),
      ]);
      if (cancelled) return;
      setCounts((prev) => ({
        ...prev,
        ...(typeof msgCount === 'number' ? { messages: msgCount } : {}),
        ...(typeof annCount === 'number' ? { announcements: annCount } : {}),
        ...(typeof classCount === 'number' ? { classes: classCount } : {}),
      }));
    };

    run();
    const id = setInterval(() => {
      if (!cancelled) run();
    }, 35000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, getLastSeen]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout').catch(() => {});
    } finally {
      try { localStorage.removeItem('token'); } catch {}
      try { localStorage.removeItem('user'); } catch {}
      try { sessionStorage.clear(); } catch {}
      if (api?.defaults?.headers?.common?.Authorization) delete api.defaults.headers.common.Authorization;
      if (typeof setUser === 'function') setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const navItems = useMemo(
   () =>
      navLinks.map((link) => ({
        key: link.key,
        label: link.name,
        path: link.path,
        icon: link.icon,
        badgeCount: counts?.[link.key] || 0,
      })),
    [navLinks, counts]
  );

  const headerTitle = (
    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main' }}>
      Hi, {displayName}
    </Typography>
  );

  const topRight = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar
        src={resolvedAvatar}
        alt={`${displayName} avatar`}
        sx={{ width: 40, height: 40, border: '2px solid rgba(15,23,42,0.12)' }}
        onError={(e) => {
          if (e.currentTarget.src !== window.location.origin + '/default.jpg') {
            e.currentTarget.src = '/default.jpg';
          }
        }}
      />
      <Button variant="contained" color="primary" onClick={handleLogout} startIcon={<FaSignOutAlt />}>
        Logout
      </Button>
    </Stack>
  );

  return (
    <AppShell
      brand="Educator"
      navItems={navItems}
      headerTitle={headerTitle}
      topRight={topRight}
      userName={displayName}
      userEmail={user?.email || ''}
      userRole="EDUCATOR"
      userAvatar={resolvedAvatar}
      collapseKey="educatorSidebarCollapsed"
    >
      {children}
    </AppShell>
  );
};

export default EducatorLayout;












